import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Bell, BellOff, CheckCircle, MapPin, Calendar, 
  Clock, Settings, Briefcase, Mail, Smartphone, Sparkles
} from 'lucide-react';
import { formatFullDateTime12h, formatTime12h, formatDateMonthDay } from '../../utils/timeFormat';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(true);

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [notifRes, prefsRes] = await Promise.all([
        fetch(`${baseUrl}/api/notifications/talent`, { headers }),
        fetch(`${baseUrl}/api/notifications/talent/preferences`, { headers }),
      ]);
      
      const notifData = await notifRes.json();
      const prefsData = await prefsRes.json();
      
      setNotifications(notifData.notifications || []);
      setUnreadCount(notifData.unread_count || 0);
      setPreferences(prefsData);
    } catch (e) {
      console.error('Error fetching notifications:', e);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${baseUrl}/api/notifications/talent/${notificationId}/read`, {
        method: 'PATCH',
        headers,
      });
      
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (e) {
      console.error('Error marking notification as read:', e);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${baseUrl}/api/notifications/talent/mark-all-read`, {
        method: 'POST',
        headers,
      });
      
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (e) {
      toast.error('Failed to mark all as read');
    }
  };

  const updatePreference = async (key, value) => {
    try {
      await fetch(`${baseUrl}/api/notifications/talent/preferences`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ [key]: value }),
      });
      
      setPreferences({ ...preferences, [key]: value });
      toast.success('Preference updated');
    } catch (e) {
      toast.error('Failed to update preference');
    }
  };

  // Iter-157a — Type-aware notification click routing.
  // Before this, every click went to `/talent/gigs/${gigId}` which had
  // no matching route → white screen. Each notification type now routes
  // to its correct detail page, with safe fallback for the shift-detail
  // path (which returns 404 → "opportunity no longer available" instead
  // of a crash).
  const openNotification = (notif) => {
    const t = notif?.notification_type || '';
    const gigId = notif?.gig_id;
    // Booking-scoped notifications (reminders, shift updates) — route to
    // the booking detail page when we have the booking id.
    if (notif?.booking_id && (
      t === 'booking_update' || t === 'shift_reminder' ||
      t === 'shift_schedule_updated' || t === 'booking_confirmed'
    )) {
      navigate(`/talent/bookings/${notif.booking_id}`);
      return;
    }
    // Replacement / urgent-fill → shift detail with replacement CTA.
    if (t === 'replacement_opportunity' || t === 'urgent_fill') {
      if (gigId) {
        navigate(`/talent/shifts/${gigId}?src=replacement`);
        return;
      }
    }
    // Default (new_gig, priority_gig, admin_invite, anything else with a
    // gig id) → shift detail page.
    if (gigId) {
      navigate(`/talent/shifts/${gigId}?src=notification`);
      return;
    }
    // No usable id → fall back to Browse Gigs.
    navigate('/talent/shifts');
  };

  const viewGig = (gigId) => {
    if (!gigId) {
      navigate('/talent/shifts');
      return;
    }
    navigate(`/talent/shifts/${gigId}?src=notification`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="notification-center">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/talent/dashboard')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <Button
            variant="outline"
            onClick={() => setShowSettings(!showSettings)}
            className="border-gray-300"
          >
            <Settings className="w-4 h-4 mr-2" />
            Preferences
          </Button>
        </div>

        {/* Notification Settings */}
        {showSettings && preferences && (
          <Card className="bg-white border-gray-200 shadow-sm mb-6">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">Notification Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Channel Preferences */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">Notification Channels</h4>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-cyan-500" />
                    <div>
                      <Label className="font-medium text-gray-900">In-App Notifications</Label>
                      <p className="text-xs text-gray-500">Receive alerts in your dashboard</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.in_app}
                    onCheckedChange={(checked) => updatePreference('notify_in_app', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-cyan-500" />
                    <div>
                      <Label className="font-medium text-gray-900">Email Notifications</Label>
                      <p className="text-xs text-gray-500">{preferences?.email_address ? `Delivered to ${preferences.email_address}` : 'Delivered to your account email'}</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="email-notifications-toggle"
                    checked={preferences.email}
                    onCheckedChange={(checked) => updatePreference('notify_email', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-5 h-5 text-cyan-500" />
                    <div>
                      <Label className="font-medium text-gray-900">SMS Notifications</Label>
                      <p className="text-xs text-gray-500">Standard text-message rates may apply</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="sms-notifications-toggle"
                    checked={preferences.sms}
                    onCheckedChange={(checked) => updatePreference('notify_sms', checked)}
                  />
                </div>
              </div>
              
              {/* Category Preferences */}
              <div className="space-y-3 pt-4 border-t">
                <h4 className="font-medium text-gray-700">Notification Categories</h4>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="font-medium text-gray-900">New Gig Alerts</Label>
                    <p className="text-xs text-gray-500">When matching gigs are posted in your area</p>
                  </div>
                  <Switch
                    checked={preferences.categories?.new_gigs}
                    onCheckedChange={(checked) => updatePreference('notify_new_gigs', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="font-medium text-gray-900">Booking Updates</Label>
                    <p className="text-xs text-gray-500">Confirmations, changes, and reminders</p>
                  </div>
                  <Switch
                    checked={preferences.categories?.booking_updates}
                    onCheckedChange={(checked) => updatePreference('notify_booking_updates', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="font-medium text-gray-900">Settlement Updates</Label>
                    <p className="text-xs text-gray-500">Payment processing and payout notifications</p>
                  </div>
                  <Switch
                    checked={preferences.categories?.payment_updates}
                    onCheckedChange={(checked) => updatePreference('notify_payment_updates', checked)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notifications List */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <CardTitle className="text-xl text-gray-900">Notifications</CardTitle>
                {unreadCount > 0 && (
                  <Badge className="bg-cyan-500 text-white">
                    {unreadCount} new
                  </Badge>
                )}
              </div>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-cyan-600 hover:text-cyan-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark all as read
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-12">
                <BellOff className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No notifications yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  You'll be notified when new gigs match your profile
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {[...notifications].sort((a, b) => {
                  // Audit-2026-08 — Personal invites (admin_invite,
                  // priority, client_invite) always render at the top
                  // of the list. Within each group we keep the newest-
                  // first ordering.
                  const ap = a.is_personal_invite ? 1 : 0;
                  const bp = b.is_personal_invite ? 1 : 0;
                  if (ap !== bp) return bp - ap;
                  return 0; // preserve backend created_at DESC order
                }).map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                      notif.is_personal_invite && !notif.is_read
                        ? 'bg-amber-50 border-amber-300'
                        : notif.is_read
                        ? 'bg-gray-50 border-gray-100'
                        : 'bg-cyan-50 border-cyan-200'
                    }`}
                    onClick={() => {
                      if (!notif.is_read) markAsRead(notif.id);
                      openNotification(notif);
                    }}
                    data-testid="notification-item"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${
                          notif.is_personal_invite && !notif.is_read
                            ? 'bg-amber-500'
                            : notif.is_read
                            ? 'bg-gray-200'
                            : 'bg-cyan-500'
                        }`}>
                          <Briefcase className={`w-4 h-4 ${
                            notif.is_read ? 'text-gray-500' : 'text-white'
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-medium ${
                              notif.is_read ? 'text-gray-700' : 'text-gray-900'
                            }`}>
                              {notif.gig_info?.role_name || 'New Gig'} at {notif.gig_info?.venue_name || 'Venue'}
                            </p>
                            {notif.is_personal_invite && (
                              <Badge
                                className="bg-amber-500 text-white border-amber-500 gap-1"
                                data-testid="personal-invite-badge"
                              >
                                <Sparkles className="w-3 h-3" /> Personal invite
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {notif.gig_info?.city}, {notif.gig_info?.state}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {notif.gig_info?.gig_date ? formatDateMonthDay(notif.gig_info.gig_date) : 'TBD'}
                            </span>
                            {notif.gig_info?.start_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTime12h(notif.gig_info.start_time)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {!notif.is_read && (
                        <div className={`w-2 h-2 rounded-full ${
                          notif.is_personal_invite ? 'bg-amber-500' : 'bg-cyan-500'
                        }`}></div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      {notif.created_at && formatFullDateTime12h(notif.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificationCenter;
